import { Listbox, Combobox, Icon } from '@shopify/polaris';
import { SearchIcon } from '@shopify/polaris-icons';
import { useState, useCallback, useEffect, useContext } from 'react';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import { SessionContext } from '#client/contexts/SessionContext.jsx';

function StoreLocationSelector({ locationId, excludeLocationId, setLocationId, onlyHasAccess = true }) {
    const { currentUser } = useContext(SessionContext);

    const [selectedOption, setSelectedOption] = useState('');
    const [inputValue, setInputValue] = useState('');
    const { data: locationEdges = [] } = useQuery({
        queryKey: ['store-locations-all', currentUser?.activeStore, onlyHasAccess],
        enabled: !!currentUser?.locationIds,
        queryFn: async () => {
            const res = await axios.get(`${import.meta.env.VITE_API_URL}/api/store-locations/all`);
            const edges = res.data?.data?.locations?.edges ?? [];
            return edges.filter((value) => !onlyHasAccess || currentUser.locationIds.indexOf(value.node.id) > -1);
        },
    });

    const allOptions = locationEdges
        .filter((location) => !excludeLocationId || excludeLocationId.indexOf(location.node.id) === -1)
        .map((location) => ({
            value: location.node.id,
            label: location.node.name,
        }));

    const filteredOptions = inputValue ? allOptions.filter((option) => option.label.toLowerCase().includes(inputValue.toLowerCase())) : allOptions;

    const escapeSpecialRegExCharacters = useCallback((value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), []);

    const updateText = useCallback(
        (value) => {
            setInputValue(value);
            // clear selection if user clears input
            if (value.trim() === '') {
                setSelectedOption('');
                setLocationId('');
            }
        },
        [allOptions, escapeSpecialRegExCharacters]
    );

    const updateSelection = useCallback(
        (selected) => {
            const matchedOption = allOptions.find((option) => {
                return option.value.match(selected);
            });

            setSelectedOption(selected);
            setLocationId(selected);
            setInputValue((matchedOption && matchedOption.label) || '');
        },
        [allOptions]
    );

    useEffect(() => {
        if (locationId !== '') {
            updateSelection(locationId);
        }
    }, [locationEdges, locationId, updateSelection]);

    const optionsMarkup =
        filteredOptions.length > 0
            ? filteredOptions.map(({ label, value }) => (
                  <Listbox.Option key={value} value={value} selected={selectedOption === value} accessibilityLabel={label}>
                      {label}
                  </Listbox.Option>
              ))
            : null;

    return (
        <Combobox
            activator={
                <Combobox.TextField
                    prefix={<Icon source={SearchIcon} />}
                    onChange={updateText}
                    label="Select Store Location"
                    labelHidden
                    value={inputValue}
                    placeholder="Select Store Location"
                    autoComplete="off"
                    readOnly={Boolean(locationId)}
                    clearButton={Boolean(locationId)}
                    onClearButtonClick={() => {
                        setSelectedOption('');
                        setLocationId('');
                        setInputValue('');
                    }}
                />
            }
        >
            {allOptions.length > 0 ? <Listbox onSelect={updateSelection}>{optionsMarkup}</Listbox> : null}
        </Combobox>
    );
}

export default StoreLocationSelector;
